import TalknAPI, { releaseSharedConnection } from '@api-client/TalknAPI';

declare global {
  interface Window {
    TalknReleaseSharedConnection?: typeof releaseSharedConnection;
  }
}

if (typeof window !== 'undefined') {
  // Only set if not already set — multiple bundles share window.TalknAPI and the patched
  // wrapper set by the first bundle must not be overwritten by subsequent bundles.
  if (!window.TalknAPI) {
    window.TalknAPI = TalknAPI;
  }

  // releaseSharedConnection must reference the same sharedConnections Map as window.TalknAPI.
  // Since each widget bundle has its own module scope, we expose it on window so all bundles
  // call the first-winner bundle's function (same closure, same Map).
  if (!window.TalknReleaseSharedConnection) {
    window.TalknReleaseSharedConnection = releaseSharedConnection;
  }

  // script タグ読み込み時に自動トラッキングを開始
  // <script src="//widgets.talkn.io"></script> を貼るだけで全ページビュー・遷移を収集
  //
  // Deferred to a microtask so all synchronous imports complete first.
  // If window.TalknWidgets is set by then, a widget bundle is present and will
  // manage its own Socket.IO connection — skip auto-tracking to avoid +1 live count.
  if (!(window as any).__talknAutoTracked) {
    (window as any).__talknAutoTracked = true;
    Promise.resolve().then(async () => {
      const hostname = window.location.hostname;
      const getConnection = () => '/' + hostname + window.location.pathname + window.location.search;
      const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';

      // contracted domain への接続に必要な API キーをパブリックエンドポイントから取得
      let trackingApiKey: string | undefined;
      if (!isLocalhost) {
        try {
          const cfgRes = await fetch(
            `https://admin.talkn.io/api/public/apps-config?domain=${encodeURIComponent(hostname)}`,
            { signal: AbortSignal.timeout(3000) }
          );
          if (cfgRes.ok) {
            const cfgs = await cfgRes.json();
            if (Array.isArray(cfgs) && cfgs.length > 0 && cfgs[0].trackingApiKey) {
              trackingApiKey = cfgs[0].trackingApiKey;
            }
          }
        } catch { /* ネットワーク失敗時はキーなしで試みる */ }
      }

      const client = await (window.TalknAPI ?? TalknAPI)(getConnection(), {
        tuneOption: {},
        ...(isLocalhost ? { host: 'localhost:10444', protocol: 'https:' } : {}),
        ...(trackingApiKey ? { apiKey: trackingApiKey } : {}),
      }).catch(() => null);

      if (!client) return;

      // SPA ナビゲーション対応: URL が変わったら retune する
      let currentConnection = getConnection();
      const handleNavigation = () => {
        const nextConnection = getConnection();
        if (nextConnection !== currentConnection) {
          currentConnection = nextConnection;
          client.retune(nextConnection).catch(() => {});
        }
      };

      // history.pushState / replaceState をラップ
      const wrapHistoryMethod = (method: 'pushState' | 'replaceState') => {
        const original = history[method].bind(history);
        history[method] = function (...args: Parameters<typeof history.pushState>) {
          original(...args);
          handleNavigation();
        };
      };
      wrapHistoryMethod('pushState');
      wrapHistoryMethod('replaceState');

      // ブラウザの戻る/進む
      window.addEventListener('popstate', handleNavigation);
    });
  }
}
